/**
 * CONNEC — Back in stock (Klaviyo)
 * ---------------------------------------------------------------------------
 * Custom element <back-in-stock> rendu par snippets/connec-back-in-stock.liquid.
 * Envoie l'inscription à l'API client Klaviyo, ce qui déclenche le flow
 * « Back In Stock Flow - Standard » au retour de l'inventaire.
 *
 * L'élément est remplacé par le thème à chaque changement de variante
 * (product:rerender), donc connectedCallback() est rejoué à chaque fois.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var API_REVISION = '2024-10-15';
  var EMAIL_STORAGE_KEY = 'connec:bis:email';
  var SUBSCRIBED_STORAGE_KEY = 'connec:bis:subscribed';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function readStorage(key, fallback) {
    try {
      var value = window.localStorage.getItem(key);
      return value === null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* stockage indisponible (navigation privée) : on ignore */
    }
  }

  /**
   * Le filtre Liquid `t` échappe les caractères HTML (l'apostrophe devient &#39;).
   * Les chaînes passent ensuite par textContent, il faut donc les décoder ici.
   */
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    var textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function subscribedVariants() {
    try {
      return JSON.parse(readStorage(SUBSCRIBED_STORAGE_KEY, '[]')) || [];
    } catch (error) {
      return [];
    }
  }

  function rememberVariant(variantId) {
    var list = subscribedVariants();
    if (list.indexOf(variantId) === -1) {
      list.push(variantId);
      writeStorage(SUBSCRIBED_STORAGE_KEY, JSON.stringify(list.slice(-50)));
    }
  }

  var BackInStock = class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-bis-trigger]');
      this.panel = this.querySelector('[data-bis-panel]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.messageElement = this.querySelector('[data-bis-message]');
      this.strings = this.parseStrings();
      this.submitLabel = this.submitButton ? this.submitButton.textContent : '';

      if (!this.trigger || !this.panel || !this.emailInput || !this.submitButton) return;

      if (!this.emailInput.value) {
        this.emailInput.value = readStorage(EMAIL_STORAGE_KEY, '');
      }

      this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
      this.submitButton.addEventListener('click', this.onSubmit.bind(this));
      this.emailInput.addEventListener('keydown', this.onKeydown.bind(this));

      if (subscribedVariants().indexOf(this.variantId) !== -1) {
        this.open();
        this.showMessage(this.strings.already, 'success');
      }
    }

    get variantId() {
      return this.getAttribute('variant-id');
    }

    get companyId() {
      return this.getAttribute('company-id');
    }

    parseStrings() {
      var defaults = {
        success: "You're on the list — we'll email you as soon as it's back.",
        already: "You're already on the list for this item.",
        error: 'Something went wrong. Please try again.',
        invalid: 'Please enter a valid email address.',
        pending: 'Sending…'
      };

      var node = this.querySelector('[data-bis-strings]');
      if (!node) return defaults;

      try {
        var parsed = JSON.parse(node.textContent);
        Object.keys(parsed).forEach(function (key) {
          parsed[key] = decodeEntities(parsed[key]);
        });
        return Object.assign(defaults, parsed);
      } catch (error) {
        return defaults;
      }
    }

    onTriggerClick() {
      if (this.panel.hidden) {
        this.open();
        this.emailInput.focus();
      } else {
        this.close();
      }
    }

    open() {
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onSubmit();
      }
    }

    showMessage(text, type) {
      if (!this.messageElement) return;
      this.messageElement.textContent = text;
      this.messageElement.hidden = !text;
      this.messageElement.classList.toggle('text-error', type === 'error');
      this.messageElement.classList.toggle('text-success', type === 'success');
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.textContent = isBusy ? this.strings.pending : this.submitLabel;
    }

    async onSubmit() {
      var email = (this.emailInput.value || '').trim();

      if (!EMAIL_PATTERN.test(email)) {
        this.showMessage(this.strings.invalid, 'error');
        this.emailInput.focus();
        return;
      }

      this.setBusy(true);
      this.showMessage('', null);

      try {
        await this.subscribe(email);

        writeStorage(EMAIL_STORAGE_KEY, email);
        rememberVariant(this.variantId);
        this.identify(email);

        this.emailInput.disabled = true;
        this.submitButton.hidden = true;
        this.showMessage(this.strings.success, 'success');

        this.dispatchEvent(
          new CustomEvent('back-in-stock:subscribed', {
            bubbles: true,
            detail: { variantId: this.variantId, email: email }
          })
        );
      } catch (error) {
        console.error('[connec-back-in-stock]', error);
        this.showMessage(this.strings.error, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    async subscribe(email) {
      var payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + this.variantId
              }
            }
          }
        }
      };

      var response = await fetch(ENDPOINT + '?company_id=' + encodeURIComponent(this.companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: API_REVISION
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        var details = await response.text();
        throw new Error('Klaviyo responded ' + response.status + ': ' + details);
      }
    }

    /** Rattache l'inscription au profil Klaviyo du navigateur (best effort). */
    identify(email) {
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({ email: email });
        }
      } catch (error) {
        /* l'onsite Klaviyo n'est pas chargé : sans importance */
      }
    }
  };

  if (!window.customElements.get('back-in-stock')) {
    window.customElements.define('back-in-stock', BackInStock);
  }
})();
